import React from 'react';
import PropTypes from 'prop-types';
import { FormattedNumber } from 'react-intl';
import { Table, Label, Icon } from 'semantic-ui-react';
import ActionMenu from '../../components/ActionMenu';
import { dateTimeCell } from '../../utilities/dateUtils';

const currencyProps = (price) => ({
  currency: 'USD',
  style: 'currency',
  minimumFractionDigits: 0,
  value: price,
});

const AWAITING_SYNC_HINT = 'This guarantee exists in APMS but Tappy has not received it yet. '
  + 'Actions are available once it syncs.';

const AwaitingSyncLabel = ({ testId }) => (
  <Label basic size="small" data-testid={testId} title={AWAITING_SYNC_HINT}>
    <Icon loading name="spinner" />
    Awaiting sync
  </Label>
);

AwaitingSyncLabel.propTypes = { testId: PropTypes.string.isRequired };

const ListRowV2 = (actionHandler) => (guaranteePurchase) => {
  const {
    externalId,
    guaranteeStatus,
    vin,
    leftLot,
    expireGuarantee,
    guaranteedAt,
    dealerNumber,
    auctionCode,
    saleNumber,
    saleLane,
    saleRun,
    modelYear,
    make,
    model,
    odometer,
    salePrice,
    productName,
    guaranteePrice,
    partner,
    paymentKind,
    guaranteePurchaseId,
  } = guaranteePurchase;

  // APMS knows the order, Tappy has not created its guarantee yet (it arrives over SQS, usually within
  // seconds). Until then the Tappy-sourced columns are empty and no action can resolve the purchase.
  const awaitingSync = !guaranteePurchaseId;
  const expireMenuLabel = expireGuarantee ? 'Unexpire Guarantee' : 'Expire Guarantee';
  const rowMenuOptions = [
    { key: 1, text: 'Edit', value: 'Edit' },
    { key: 2, text: 'Extend Guarantee', value: 'Extend Guarantee' },
    { key: 3, text: 'Cancel Guarantee', value: 'Cancel Guarantee' },
    { key: 4, text: 'Status History', value: 'Audit' },
    { key: 5, text: 'Vehicle Status Tracker', value: 'Vehicle Status Tracker' },
    { key: 6, text: expireMenuLabel, value: expireMenuLabel },
  ];

  const auctionInfo = [auctionCode, saleNumber, saleLane, saleRun].join(' / ');
  const vehicle = [modelYear, make, model].filter(Boolean).join(' ');

  const handleActionChange = (e, { value }) => {
    e.preventDefault();
    actionHandler(value, guaranteePurchase);
  };

  const labels = [];
  if (leftLot) {
    labels.push(
      <Label key="left lot" color="red" horizontal>
        Left Lot
      </Label>,
    );
  }
  if (expireGuarantee) {
    labels.push(
      <Label key="Expired" color="red" horizontal>
        Expired
      </Label>,
    );
  }

  return (
    <Table.Row key={externalId} data-testid="purchase-row">
      <Table.Cell>
        {vin} {labels}
      </Table.Cell>
      <Table.Cell>{externalId}</Table.Cell>
      <Table.Cell data-testid="guaranteed-at-cell">
        {awaitingSync && !guaranteedAt
          ? <AwaitingSyncLabel testId="awaiting-sync-guaranteed-at" />
          : dateTimeCell(guaranteedAt)}
      </Table.Cell>
      <Table.Cell data-testid="dealer-number-cell">{dealerNumber}</Table.Cell>
      <Table.Cell>{auctionInfo}</Table.Cell>
      <Table.Cell>{vehicle}</Table.Cell>
      <Table.Cell>{odometer}</Table.Cell>
      <Table.Cell>
        {salePrice != null &&
          React.createElement(FormattedNumber, currencyProps(salePrice))}
      </Table.Cell>
      <Table.Cell data-testid="status-cell">
        {awaitingSync && !guaranteeStatus
          ? <AwaitingSyncLabel testId="awaiting-sync-label" />
          : guaranteeStatus}
      </Table.Cell>
      <Table.Cell data-testid="product-cell">{productName}</Table.Cell>
      <Table.Cell>
        {guaranteePrice != null &&
          React.createElement(FormattedNumber, currencyProps(guaranteePrice))}
      </Table.Cell>
      <Table.Cell data-testid='partner-cell'>{partner}</Table.Cell>
      <Table.Cell>{paymentKind}</Table.Cell>
      <Table.Cell>
        <ActionMenu
          options={rowMenuOptions}
          onChange={handleActionChange}
          disabled={awaitingSync}
        />
      </Table.Cell>
    </Table.Row>
  );
};

export default ListRowV2;
